// ── Home-screen widget data sync (Android) ───────────────────────────────────
// Pushes live data to each native widget via WidgetBridgePlugin, and pulls
// back any habit / task checkbox taps recorded while the app was closed so they
// can be applied through the app's normal paths (keeping Firestore sync
// consistent). No-ops entirely outside a native build.
import { useEffect, useRef, useState } from 'react';
import { intervalLabel } from '../utils/habitInterval';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { todayStr, fmt, localDateStr } from '../utils/dateHelpers';
import { isOccurringOn, isTaskOccurringOn, isTaskDoneOn, toggleTaskDoneOn } from '../utils/taskLogic';
import { DEFAULT_TASK_COLOR, DEFAULT_EVENT_COLOR } from '../constants/palettes';
import { loadWidgetColor, widgetThemeFor, vibrantAccent, WIDGET_COLOR_EVENT } from './widgetColor';
import { todoWeeklyProgress, goalOn } from '../utils/habitWeeklyGoal';
import { isHabitDueOn, isHabitDoneOn, isWithinHabitRange, restrictedDayColor, HABIT_PERIOD_DAYS, isHabitExcludedOn } from '../utils/habitLogic';
import { vividPastel } from '../theme/colorEngine';
import { focusWeekRange, sliceNameForSession, sliceColor } from '../utils/focusDonut';

const WidgetBridge = registerPlugin('WidgetBridge');
const isNative = Capacitor.isNativePlatform();

function freqLabelFor(h) {
  if (h.habitType === 'nottodo') return 'Avoid daily';
  if (h.habitType === 'limited') return 'Limited';
  if (h.frequencyType === 'count') return `${h.weeklyCount || 1}× a week`;
  if (h.frequencyType === 'interval') return intervalLabel(h);
  if (h.frequencyType === 'specific') return `${(h.days || []).length || 1}× a week`;
  if (h.frequencyType === 'monthly') {
    return h.monthlyMode === 'count'
      ? `${h.monthlyCount || 1}× a month`
      : `${(h.monthDates || []).length || 1}× a month`;
  }
  if (h.frequencyType === 'yearly') return `${h.yearlyCount || 1}× a year`;
  return 'Every day';
}

// ── Pure payload builders (exported so they can be unit-tested) ──────────────

const PLANNER_MAX_PER_DAY = 60;
// The widget's < > browse weeks, so it carries 4 weeks back and 8 ahead of this
// week (also covering week rollovers before the app is next opened). Past that
// it asks to be opened.
const PLANNER_WEEKS_BACK = 4;
const PLANNER_SPAN_DAYS = 12 * 7;

// Same colour rules as the Planner (CalendarTab's itemColor/taskColor/eventColor):
// occurrence override, then category, then the item's own colour, then default.
function plannerItemColor(item, kind, ov, categories) {
  if (ov?.color) return ov.color;
  const catId = kind === 'task' ? item.goalId : item.categoryId;
  const cat = catId ? (categories || []).find((c) => c.id === catId) : null;
  if (cat?.color) return cat.color;
  return item.color || (kind === 'task' ? DEFAULT_TASK_COLOR : DEFAULT_EVENT_COLOR);
}

// Planner widget: every task and event on each day of the window, with
// per-occurrence time/colour overrides and done-state applied, untimed ones
// first then by time. Ticked tasks keep their place (Jade's call).
// Dates are LOCAL strings via fmt() — toISOString() is UTC and would put
// everything a day early before ~8am in Manila.
export function buildPlannerPayload({
  tasks = [], calEvents = [], taskOccurrenceOverrides = {}, calEventOccurrenceOverrides = {},
  categories = [], weekStart = 'sunday', now = new Date(),
}) {
  const mondayFirst = weekStart === 'monday';
  const dow = now.getDay();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (mondayFirst ? (dow + 6) % 7 : dow) - PLANNER_WEEKS_BACK * 7);
  const days = {};
  let from = '';
  let to = '';
  for (let i = 0; i < PLANNER_SPAN_DAYS; i++) {
    const ds = fmt(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    if (i === 0) from = ds;
    to = ds;
    const items = [];
    for (const t of tasks || []) {
      if (!isTaskOccurringOn(t, ds)) continue;
      const ov = taskOccurrenceOverrides[`${t.id}:${ds}`] || {};
      items.push({
        id: String(t.id), kind: 'task', title: t.title || '',
        time: ov.time ?? t.time ?? '', endTime: ov.endTime ?? t.endTime ?? '',
        done: isTaskDoneOn(t, ds, taskOccurrenceOverrides),
        color: plannerItemColor(t, 'task', ov, categories),
      });
    }
    for (const e of calEvents || []) {
      if (!isOccurringOn(e, ds)) continue;
      const ov = calEventOccurrenceOverrides[`${e.id}:${ds}`] || {};
      items.push({
        id: String(e.id), kind: 'event', title: e.title || '',
        time: ov.time ?? e.time ?? '', endTime: ov.endTime ?? e.endTime ?? '',
        color: plannerItemColor(e, 'event', ov, categories),
      });
    }
    if (!items.length) continue;
    items.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    days[ds] = items.slice(0, PLANNER_MAX_PER_DAY);
  }
  return { weekStart: mondayFirst ? 'monday' : 'sunday', from, to, days };
}

// Planner · Month widget: which days have at least one task or event (the dot
// under a date), from the start of the month 6 months back to the end of the
// month 12 ahead — the range its ‹ › can page through before it asks for the
// app. Same occurrence rules as the Planner widget (isTaskOccurringOn /
// isOccurringOn). The calendar itself is drawn on the phone from its own clock.
export const MONTH_WIDGET_BACK = 6;
export const MONTH_WIDGET_AHEAD = 12;

export function buildPlannerMonthPayload({ tasks = [], calEvents = [], weekStart = 'sunday', now = new Date() }) {
  const first = new Date(now.getFullYear(), now.getMonth() - MONTH_WIDGET_BACK, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + MONTH_WIDGET_AHEAD + 1, 0);
  const busy = [];
  for (let d = first; d <= last; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    const ds = fmt(d);
    if ((tasks || []).some((t) => isTaskOccurringOn(t, ds)) || (calEvents || []).some((e) => isOccurringOn(e, ds))) busy.push(ds);
  }
  return { weekStart: weekStart === 'monday' ? 'monday' : 'sunday', from: fmt(first), to: fmt(last), busy };
}

// ── Theme colours for the widgets ─────────────────────────────────────────────
// Read straight off the CSS variables the app is painted with (on .clarity-main,
// where the background themes override them), so the widget matches whatever
// theme/accent is chosen without keeping a second copy of every theme here.
// Widgets can't do transparency over the app's gradients, so anything with
// alpha is flattened onto the surface it sits on.

function parseCssColor(value, ctx) {
  if (!value || !ctx) return null;
  ctx.fillStyle = '#000000';
  ctx.fillStyle = value.trim();
  const out = ctx.fillStyle; // "#rrggbb" or "rgba(r, g, b, a)"
  if (out.startsWith('#')) {
    return { r: parseInt(out.slice(1, 3), 16), g: parseInt(out.slice(3, 5), 16), b: parseInt(out.slice(5, 7), 16), a: 1 };
  }
  const m = out.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const [r, g, b, a = '1'] = m[1].split(',').map((x) => x.trim());
  return { r: +r, g: +g, b: +b, a: +a };
}

export function flattenColor(c, base = { r: 255, g: 255, b: 255 }) {
  const mix = (x, y) => Math.round(x * c.a + y * (1 - c.a));
  const hex = (n) => n.toString(16).padStart(2, '0');
  return `#${hex(mix(c.r, base.r))}${hex(mix(c.g, base.g))}${hex(mix(c.b, base.b))}`;
}

export function readWidgetTheme() {
  try {
    const el = document.querySelector('.clarity-main') || document.documentElement;
    const css = getComputedStyle(el);
    const ctx = document.createElement('canvas').getContext('2d');
    const get = (name) => parseCssColor(css.getPropertyValue(name), ctx);
    const panel = get('--c-bg-card') || { r: 255, g: 255, b: 255, a: 1 };
    const panelFlat = parseCssColor(flattenColor(panel), ctx);
    let bg = get('--c-bg');
    // The Default theme's background is plain white, which would make the panel
    // vanish into the card — use its soft accent tint instead.
    if (!bg || (bg.r > 245 && bg.g > 245 && bg.b > 245)) bg = get('--c-bg-active') || bg;
    const out = {
      bg: bg && flattenColor(bg),
      panel: flattenColor(panel),
      text: get('--c-text') && flattenColor(get('--c-text'), panelFlat),
      textSoft: get('--c-text-soft') && flattenColor(get('--c-text-soft'), panelFlat),
      accent: get('--c-primary') && flattenColor(get('--c-primary')),
      onAccent: get('--c-on-primary') && flattenColor(get('--c-on-primary')),
      divider: get('--c-border') && flattenColor(get('--c-border'), panelFlat),
    };
    Object.keys(out).forEach((k) => { if (!out[k]) delete out[k]; });
    return out;
  } catch {
    return null;
  }
}

// Habits widget: today's checklist — To-Do habits due today (plus "N times a
// week" ones, which have no fixed day). The week's x/target is exactly what the
// app's Weekly Goal shows (todoWeeklyProgress): the habit's own 7-day week from
// its start date, nothing logged before that start counted. `accent` is a
// vibrant version of the habit's colour for its bar and checkbox.
export function buildHabitsPayload(habits, habitLogs = {}, habitSkips = [], today = todayStr()) {
  const items = (habits || [])
    .filter((h) => (h.habitType || 'todo') === 'todo')
    .filter((h) => isWithinHabitRange(h, today))
    .filter((h) => h.frequencyType === 'count' || isHabitDueOn(h, today))
    .map((h) => {
      const { completed, target } = todoWeeklyProgress(h, habitLogs, today);
      const color = h.color || '#d4789a';
      const rawLog = habitLogs[today]?.[h.id];
      return {
        id: String(h.id),
        name: h.name,
        freq: freqLabelFor(h),
        color,
        accent: vibrantAccent(color),
        emoji: h.emoji || '⭐',
        done: isHabitDoneOn(h, habitLogs, today, habitSkips),
        target,
        completed: Math.min(target, completed),
        // Per-day tally for habits logged more than once a day (e.g. "brush
        // teeth 2x/day") — lets the widget show a "+" tap target until
        // today's count actually reaches dailyCount, instead of marking it
        // done after a single tap.
        dailyCount: h.dailyCount || 1,
        todayCount: typeof rawLog === 'number' ? rawLog : (rawLog ? 1 : 0),
      };
    });
  return { items };
}

// Clock-In donut widget: minutes per category for each day from the start of
// this week (Settings → "Week starts on") through the end of next week, plus
// each category's slice colour. The widget sums the days of whichever week the
// phone's clock is in — so it rolls over to an empty new week on its own,
// without the app running (nothing can be logged while the app is closed).
// Slices, colours and the week all come from utils/focusDonut, the same code
// the app's donut uses.
export function buildFocusDonutPayload({ focusSessions = [], categories = [], weekStart = 'sunday', now = new Date() }) {
  const { start, from } = focusWeekRange(weekStart, 0, now);
  const to = fmt(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 13));
  const days = {};
  const colors = {};
  (focusSessions || []).forEach((s) => {
    if (!s?.date || s.date < from || s.date > to || !(s.minutes > 0)) return;
    const name = sliceNameForSession(s, categories);
    const day = days[s.date] || (days[s.date] = {});
    day[name] = (day[name] || 0) + s.minutes;
    if (!colors[name]) colors[name] = sliceColor(name, categories);
  });
  return { weekStart: weekStart === 'monday' ? 'monday' : 'sunday', from, to, days, colors };
}

// ── Habit Tracker widget ─────────────────────────────────────────────────────
// One habit per widget, drawn as the app's Month tracker card (ProfileTab's
// renderMonthCard): the habit's current 28-day period counted from its start
// date, 6 blocks across, done days in the habit's vivid pastel, missed days
// grey, restricted habits in restrictedDayColor.
//
// Per habit: `days` holds only the days whose block isn't `defaultPast` (sparse
// — most to-do days are either done or not, most restricted ones are green),
// from `from` (one period back) to today. Days after the last sync fall back
// to `defaultPast`, which is right for both kinds: an unlogged to-do day is
// missed, an unslipped restricted day is green. The widget works out the
// period, today and future days from the phone's own clock.
export const TRACKER_MISSED_COLOR = '#ddd8ea'; // ProfileTab month card, unchecked
export const TRACKER_LOOKBACK_DAYS = HABIT_PERIOD_DAYS;

export function buildHabitTrackerPayload({
  habits = [], habitLogs = {}, habitSkips = [], categories = [], today = todayStr(),
}) {
  const t = new Date(today + 'T00:00:00');
  const from = fmt(new Date(t.getFullYear(), t.getMonth(), t.getDate() - TRACKER_LOOKBACK_DAYS));
  const items = (habits || []).map((h) => {
    const cat = (categories || []).find((c) => c.id === h.goalId);
    const color = vividPastel(cat?.color || h.color || '#d4789a');
    const start = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : today);
    const restricted = h.habitType === 'nottodo' || h.habitType === 'limited';
    const target = h.dailyCount || 1;
    const defaultPast = restricted ? '#7DC87A' : TRACKER_MISSED_COLOR;
    const days = {};
    const s = new Date(Math.max(new Date(start + 'T00:00:00').getTime(), new Date(from + 'T00:00:00').getTime()));
    for (let d = s; fmt(d) <= today; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      const ds = fmt(d);
      if (isHabitExcludedOn(h, ds)) continue; // drawn from `excluded` instead
      let c;
      if (restricted) {
        c = restrictedDayColor(h, ds, habitSkips || []);
      } else {
        const v = habitLogs?.[ds]?.[h.id];
        const done = typeof v === 'number' ? v >= (goalOn(h, ds).dailyCount || 1) : !!v;
        c = done ? color : TRACKER_MISSED_COLOR;
      }
      if (c !== defaultPast) days[ds] = c;
    }
    const raw = habitLogs?.[today]?.[h.id];
    return {
      id: String(h.id),
      name: h.name || 'Habit',
      emoji: h.emoji || '⭐',
      color,
      // The "(2x)" after the name: a vibrant version of the habit's own colour,
      // the same as the app's cards (Jade, round 3: the plain one was too pale).
      nameColor: vibrantAccent(h.color || '#d4789a'),
      start,
      // Only to-do habits get the check button; restricted ones are logged as slips in the app.
      todo: !restricted,
      dailyCount: target,
      todayCount: typeof raw === 'number' ? raw : (raw ? 1 : 0),
      defaultPast,
      days,
      // Excluded dates (days off) from `from` on, future ones included so a
      // day off that arrives while the app is closed is still drawn right.
      // The widget draws them faded like a future day, as the app's card does.
      excluded: (h.excludedDates || []).filter((ds) => ds >= from && isHabitExcludedOn(h, ds)).sort(),
    };
  });
  return { from, items };
}

// Tapping a date on the Planner · Month widget opens that day in the Planner:
// a one-shot flag for a CalendarTab that mounts with the tab switch, an event
// for one already mounted (CalendarTab clears the flag either way).
export const PLANNER_OPEN_DATE_FLAG = 'clarity_calendar_openDate';
export const PLANNER_OPEN_DATE_EVENT = 'clarity-calendar-open-date';

// Parses a widget tap action ("planner", "planner_day:2026-10-03", …).
export function parseWidgetAction(action) {
  const str = String(action || '');
  const i = str.indexOf(':');
  return i < 0 ? { name: str, arg: '' } : { name: str.slice(0, i + 1), arg: str.slice(i + 1) };
}

export function useWidgetSync({
  tasks, calEvents, taskOccurrenceOverrides, calEventOccurrenceOverrides, categories,
  habits, habitLogs, habitSkips, weekStart, themeKey, focusSessions,
}) {
  // Colours for every widget: the app's theme, or the pastel picked in
  // Settings → Widget color (widgetColor.js). Re-read on any theme change.
  const [widgetColor, setWidgetColor] = useState(loadWidgetColor);
  useEffect(() => {
    const onChange = () => setWidgetColor(loadWidgetColor());
    window.addEventListener(WIDGET_COLOR_EVENT, onChange);
    return () => window.removeEventListener(WIDGET_COLOR_EVENT, onChange);
  }, []);
  useEffect(() => {
    if (!isNative) return;
    const theme = widgetThemeFor(widgetColor, readWidgetTheme);
    if (theme) WidgetBridge.setData({ widget: 'theme', json: JSON.stringify(theme) }).catch(() => {});
  }, [widgetColor, themeKey]);

  // Planner widget: the week window (see PLANNER_SPAN_DAYS). Re-sent when the
  // day changes too (`today`), so the window keeps up with the calendar.
  const today = todayStr();
  useEffect(() => {
    if (!isNative) return;
    const payload = buildPlannerPayload({
      tasks, calEvents, taskOccurrenceOverrides, calEventOccurrenceOverrides, categories, weekStart,
    });
    WidgetBridge.setData({ widget: 'planner', json: JSON.stringify(payload) }).catch(() => {});
  }, [tasks, calEvents, taskOccurrenceOverrides, calEventOccurrenceOverrides, categories, weekStart, today]);

  // Planner · Month widget: busy days (see buildPlannerMonthPayload).
  useEffect(() => {
    if (!isNative) return;
    const payload = buildPlannerMonthPayload({ tasks, calEvents, weekStart });
    WidgetBridge.setData({ widget: 'plannerMonth', json: JSON.stringify(payload) }).catch(() => {});
  }, [tasks, calEvents, weekStart, today]);

  // Habits widget: see buildHabitsPayload.
  useEffect(() => {
    if (!isNative) return;
    WidgetBridge.setData({ widget: 'habits', json: JSON.stringify(buildHabitsPayload(habits, habitLogs || {}, habitSkips || [])) }).catch(() => {});
  }, [habits, habitLogs, habitSkips]);

  // Clock-In donut widget: see buildFocusDonutPayload. Re-sent when the day
  // changes too, so the window follows the calendar.
  useEffect(() => {
    if (!isNative) return;
    const payload = buildFocusDonutPayload({ focusSessions: focusSessions || [], categories, weekStart });
    WidgetBridge.setData({ widget: 'focusDonut', json: JSON.stringify(payload) }).catch(() => {});
  }, [focusSessions, categories, weekStart, today]);

  // Habit Tracker widgets: every habit, so each widget can show the one it was
  // set to (see buildHabitTrackerPayload). Re-sent when the day changes.
  useEffect(() => {
    if (!isNative) return;
    const payload = buildHabitTrackerPayload({ habits, habitLogs: habitLogs || {}, habitSkips: habitSkips || [], categories });
    WidgetBridge.setData({ widget: 'habitTracker', json: JSON.stringify(payload) }).catch(() => {});
  }, [habits, habitLogs, habitSkips, categories, today]);
}

// Applies any habit checkboxes tapped on the widget while the app was closed,
// through the same setHabitLogs path the in-app checkbox uses, then clears
// them so they aren't re-applied next time. Pending values are the raw
// per-day log count the widget computed (0/1 for simple habits, 0..dailyCount
// for multi-count ones), so they drop straight into habitLogs as-is.
async function reconcilePendingHabitToggles(setHabitLogs) {
  if (!isNative) return;
  try {
    const res = await WidgetBridge.getPendingHabitToggles();
    const pending = JSON.parse(res?.json || '{}');
    const ids = Object.keys(pending);
    if (!ids.length) return;
    const today = todayStr();
    setHabitLogs((prev) => ({
      ...prev,
      [today]: { ...(prev[today] || {}), ...pending },
    }));
    await WidgetBridge.clearPendingHabitToggles();
  } catch { /* best-effort */ }
}

export function useWidgetPendingToggles(setHabitLogs) {
  useEffect(() => {
    if (!isNative) return;
    reconcilePendingHabitToggles(setHabitLogs);
    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) reconcilePendingHabitToggles(setHabitLogs);
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// Applies task checkboxes ticked on the Planner widget while the app was closed
// (keys "<taskId>|<date>" -> done), through toggleTaskDoneOn with an explicit
// done value — so a one-time task sets `done` and a recurring one sets just that
// day's occurrence, exactly as ticking it in the app would. Only the ticks
// applied are cleared, so one made meanwhile on the widget survives.
export function applyPendingTaskToggles(pending, tasks, setTasks, setTaskOccurrenceOverrides) {
  const applied = {};
  Object.entries(pending || {}).forEach(([key, done]) => {
    const i = key.lastIndexOf('|');
    if (i < 0) return;
    const id = key.slice(0, i);
    const date = key.slice(i + 1);
    const task = (tasks || []).find((t) => String(t.id) === id);
    if (task) toggleTaskDoneOn(task, date, setTasks, setTaskOccurrenceOverrides, !!done);
    applied[key] = !!done; // a deleted task's tick is dropped, not kept forever
  });
  return applied;
}

export function useWidgetPendingTaskToggles(tasks, setTasks, setTaskOccurrenceOverrides) {
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  useEffect(() => {
    if (!isNative) return;
    const reconcile = async () => {
      try {
        const res = await WidgetBridge.getPendingTaskToggles();
        const pending = JSON.parse(res?.json || '{}');
        if (!Object.keys(pending).length) return;
        const applied = applyPendingTaskToggles(pending, tasksRef.current, setTasks, setTaskOccurrenceOverrides);
        await WidgetBridge.clearPendingTaskToggles({ applied: JSON.stringify(applied) });
      } catch { /* best-effort */ }
    };
    reconcile();
    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) reconcile();
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// Collects the action a widget tap asked for ("planner_add_task", "habits", …) once
// on start-up and again every time the app returns to the foreground — a tap on
// an already-running app arrives as a resume, not a launch. `onAction` is read
// through a ref, so it always sees the latest render's state.
export function useWidgetLaunchActions(onAction) {
  const handlerRef = useRef(onAction);
  handlerRef.current = onAction;
  useEffect(() => {
    if (!isNative) return;
    const collect = async () => {
      try {
        const res = await WidgetBridge.takeLaunchAction();
        if (res?.action) handlerRef.current(res.action);
      } catch { /* best-effort */ }
    };
    collect();
    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) collect();
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, []);
}
